import React, { useEffect, useRef, useState } from "react";

interface DesktopCameraProps {
  onConfirm: (file: File) => void;
  onClose: () => void;
}

const DesktopCamera: React.FC<DesktopCameraProps> = ({
  onConfirm,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 🎥 Start camera
  useEffect(() => {
    if (previewUrl) return;

    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });

        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        setError("Camera access denied or unavailable");
      }
    };

    startCamera();

    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [previewUrl]);

  // 📸 Capture image
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        setCapturedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));

        // stop camera once captured
        stream?.getTracks().forEach((t) => t.stop());
      },
      "image/jpeg",
      0.95,
    );
  };

  // ✅ Confirm submit
  const confirmPhoto = () => {
    if (!capturedBlob) return;

    const file = new File([capturedBlob], `photo-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });

    onConfirm(file);
    onClose();
  };

  // 🔄 Retake
  const retakePhoto = () => {
    setPreviewUrl(null);
    setCapturedBlob(null);
  };

  return (
    <div className="camera-overlay">
      <div className="camera-modal">
        <h3>{previewUrl ? "Preview" : "Camera"}</h3>

        {error && <p style={{ color: "red" }}>{error}</p>}

        {!previewUrl ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", borderRadius: 8 }}
          />
        ) : (
          <img
            src={previewUrl}
            alt="Preview"
            style={{ width: "100%", borderRadius: 8 }}
          />
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* <div className="camera-actions">
          {!previewUrl ? (
            <>
              <button className="btn btn--teal" onClick={capturePhoto}>
                Capture
              </button>
              <button className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="btn btn--teal" onClick={confirmPhoto}>
                Confirm
              </button>
              <button className="btn btn--secondary" onClick={retakePhoto}>
                Retake
              </button>
            </>
          )}
        </div> */}

        <div
          className="camera-actions"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            width: "100%",
          }}
        >
          {!previewUrl ? (
            <>
              <button className="btn btn--teal" onClick={capturePhoto}>
                Capture
              </button>

              <button className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="btn btn--teal" onClick={confirmPhoto}>
                Confirm
              </button>

              <button className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopCamera;
